/**
 * useAccess - Single Source of Truth for Access Control
 * 
 * Returns all authorization state needed by route guards:
 * - isAuthenticated: user is logged in
 * - isEmailVerified: email has been verified (via auth.users.email_confirmed_at)
 * - isSubscriber: has active subscription entitlement
 * - accountStatus: active | suspended | deleted_requested
 * - loading: true while any of the above are being determined
 * 
 * CRITICAL: This hook must be used by ALL route guards. No duplicated logic per page.
 */

import { useEffect, useState, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { useQueryClient } from '@tanstack/react-query';
 import { 
   type AccountStatus as AccountStatusType,
   type AccessErrorCode,
   ACCESS_ERROR_CODES,
   ACCOUNT_STATUS,
 } from '@/lib/constants';

 export type AccountStatus = AccountStatusType | null;

export interface AccessState {
  isAuthenticated: boolean;
  isEmailVerified: boolean;
  isSubscriber: boolean;
  accountStatus: AccountStatus;
  loading: boolean;
  // Convenience flags for route guards
  canAccessBuyerRoutes: boolean;
  canAccessSubscriberRoutes: boolean;
  isSuspended: boolean;
   // Computed access state
   hasFullAccess: boolean;
   // Error code for debugging/display (null if has full access)
   errorCode: AccessErrorCode | null;
  // For debugging/display
  userId: string | null;
  userEmail: string | null;
}

// Cache for access state to avoid duplicate DB calls
const accessCache = new Map<string, { 
  isEmailVerified: boolean; 
  isSubscriber: boolean; 
  accountStatus: AccountStatus;
  timestamp: number;
}>();
const CACHE_TTL = 30 * 1000; // 30 seconds (shorter than role cache since it can change)

export function useAccess(): AccessState {
  const { user, session, isLoading: authLoading } = useAuth();
  const queryClient = useQueryClient();
  
  const [isEmailVerified, setIsEmailVerified] = useState(false);
  const [isSubscriber, setIsSubscriber] = useState(false);
  const [accountStatus, setAccountStatus] = useState<AccountStatus>(null);
  const [accessLoading, setAccessLoading] = useState(true);

  // Force refresh access state (call after verification or subscription change)
  const refreshAccessState = useCallback(async () => {
    if (!user) return;
    
    // Clear cache for this user
    accessCache.delete(user.id);
    
    // Re-fetch access state
    await fetchAccessState(user.id);
  }, [user]);

  // Fetch access state from database
  const fetchAccessState = useCallback(async (userId: string) => {
    // Check cache first
    const cached = accessCache.get(userId);
    if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
      setIsEmailVerified(cached.isEmailVerified);
      setIsSubscriber(cached.isSubscriber);
      setAccountStatus(cached.accountStatus);
      setAccessLoading(false);
      return;
    }

    try {
      // Use RPC functions for email verification and subscriber status
      // These match the DB functions with default auth.uid() parameters
      const [emailResult, subscriberResult, profileResult] = await Promise.all([
        supabase.rpc('is_email_verified'),
        supabase.rpc('is_subscriber'),
        supabase
          .from('buyer_profiles')
          .select('status')
          .eq('id', userId)
          .maybeSingle(),
      ]);

      const emailVerified = emailResult.data === true;
      const subscriber = subscriberResult.data === true;
      const status = (profileResult.data?.status as AccountStatus) || 'active';

      // Cache the results
      accessCache.set(userId, {
        isEmailVerified: emailVerified,
        isSubscriber: subscriber,
        accountStatus: status,
        timestamp: Date.now(),
      });

      setIsEmailVerified(emailVerified);
      setIsSubscriber(subscriber);
      setAccountStatus(status);
    } catch (error) {
      console.error('[useAccess] Error fetching access state:', error);
      // Default to safe values
      setIsEmailVerified(false);
      setIsSubscriber(false);
      setAccountStatus('active');
    } finally {
      setAccessLoading(false);
    }
  }, []);

  // Listen for auth state changes and refresh access
  useEffect(() => {
    if (authLoading) return;

    if (!user) {
      // Reset state when logged out
      setIsEmailVerified(false);
      setIsSubscriber(false);
      setAccountStatus(null);
      setAccessLoading(false);
      return;
    }

    // Fetch access state for logged-in user
    setAccessLoading(true);
    fetchAccessState(user.id);
  }, [user, authLoading, fetchAccessState]);

  // Also listen for session refresh (e.g., after email verification)
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'USER_UPDATED' || event === 'TOKEN_REFRESHED') {
        // User was updated (possibly email verified), refresh access state
        if (user) {
          accessCache.delete(user.id);
          fetchAccessState(user.id);
        }
      }
    });

    return () => subscription.unsubscribe();
  }, [user, fetchAccessState]);

  // Expose refresh function via query client for external invalidation
  useEffect(() => {
    // Store refresh function in query client metadata for external access
    queryClient.setQueryData(['access-refresh'], refreshAccessState);
  }, [queryClient, refreshAccessState]);

  const isAuthenticated = !!user;
  const isSuspended = accountStatus === 'suspended' || accountStatus === 'deleted_requested';
  
  // Derived convenience flags
  // Buyer routes: authenticated + verified + not suspended
  const canAccessBuyerRoutes = isAuthenticated && isEmailVerified && !isSuspended;
  
  // Subscriber routes: buyer routes + active subscription
  const canAccessSubscriberRoutes = canAccessBuyerRoutes && isSubscriber;

  return {
    isAuthenticated,
    isEmailVerified,
    isSubscriber,
    accountStatus,
    loading: authLoading || accessLoading,
    canAccessBuyerRoutes,
    canAccessSubscriberRoutes,
    isSuspended,
     hasFullAccess: canAccessBuyerRoutes,
     errorCode: !isAuthenticated 
       ? null  // Not an error, just not logged in
       : isSuspended 
         ? ACCESS_ERROR_CODES.ACCOUNT_SUSPENDED
         : !isEmailVerified 
           ? ACCESS_ERROR_CODES.EMAIL_NOT_VERIFIED
           : null,
    userId: user?.id ?? null,
    userEmail: user?.email ?? null,
  };
}

// Export cache clear function for logout
export function clearAccessCache(userId?: string) {
  if (userId) {
    accessCache.delete(userId);
  } else {
    accessCache.clear();
  }
}
